// src/components/EnOcean/ButtonTestDialog.jsx
// Dialog for testing emergency buttons at a location
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
  TextField,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  RadioButtonChecked as ButtonIcon,
  SignalCellularAlt as SignalIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  SkipNext as SkipIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  HourglassEmpty as PendingIcon,
  TouchApp as TouchAppIcon,
  MoreVert as MoreIcon,
  MeetingRoom as RoomIcon,
  Download as DownloadIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { buttonTestsApi, locationsApi } from '../../services/apiClient';
import enoceanService from '../../services/enoceanService';

// Signal quality indicators
const getSignalInfo = (rssiDbm) => {
  if (rssiDbm === null || rssiDbm === undefined) {
    return { label: 'Unknown', color: 'default', icon: null };
  }
  if (rssiDbm >= -50) return { label: 'Excellent', color: 'success', bars: 4 };
  if (rssiDbm >= -60) return { label: 'Good', color: 'success', bars: 3 };
  if (rssiDbm >= -70) return { label: 'Fair', color: 'warning', bars: 2 };
  if (rssiDbm >= -80) return { label: 'Poor', color: 'error', bars: 1 };
  return { label: 'Very Poor', color: 'error', bars: 0 };
};

const ButtonTestDialog = ({
  open,
  onClose,
  locationId,
  locationName,
  onTestComplete
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Test state
  const [isTestActive, setIsTestActive] = useState(false);
  const [testUUID, setTestUUID] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [testedCount, setTestedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [totalButtons, setTotalButtons] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Naming suggestions
  const [namingSuggestions, setNamingSuggestions] = useState({ rooms: [], persons: [] });

  // Edit name state
  const [editingButtonId, setEditingButtonId] = useState(null);
  const [editName, setEditName] = useState('');
  const [nameMenuAnchor, setNameMenuAnchor] = useState(null);
  const [selectedButtonForNaming, setSelectedButtonForNaming] = useState(null);

  // Skip reason state
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [buttonToSkip, setButtonToSkip] = useState(null);

  // Room linking state for fixed buttons
  const [roomLinkDialogOpen, setRoomLinkDialogOpen] = useState(false);
  const [buttonForRoomLink, setButtonForRoomLink] = useState(null);
  const [linkedRooms, setLinkedRooms] = useState([]);

  // Cooldown state
  const [isInCooldown, setIsInCooldown] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef(null);

  // WebSocket unsubscribe function
  const unsubscribeRef = useRef(null);

  // WebSocket event handlers
  const handleButtonTested = useCallback((data) => {
    if (data.testUUID !== testUUID) return;
    console.log('[ButtonTest WS] Button tested:', data);

    setButtons(prev => prev.map(btn =>
      btn.resultId === data.resultId
        ? { ...btn, status: 'success', testedAt: data.testedAt, rssiDbm: data.rssiDbm, signalQuality: data.signalQuality, antennaName: data.antennaName, channel: data.channel }
        : btn
    ));
    setTestedCount(prev => prev + 1);
    setSuccess(`Button "${data.friendlyName || data.hexCode}" tested successfully!`);
  }, [testUUID]);

  const handleButtonSkipped = useCallback((data) => {
    if (data.testUUID !== testUUID) return;
    console.log('[ButtonTest WS] Button skipped:', data);

    setButtons(prev => prev.map(btn =>
      btn.resultId === data.resultId
        ? { ...btn, status: 'skipped', skipReason: data.reason || data.skipReason }
        : btn
    ));
    setSkippedCount(prev => prev + 1);
  }, [testUUID]);

  const handleButtonRenamed = useCallback((data) => {
    if (data.testUUID !== testUUID) return;
    console.log('[ButtonTest WS] Button renamed:', data);

    setButtons(prev => prev.map(btn =>
      btn.resultId === data.resultId
        ? { ...btn, friendlyName: data.friendlyName }
        : btn
    ));
  }, [testUUID]);

  const handleButtonRoomLinked = useCallback((data) => {
    if (data.testUUID !== testUUID) return;
    console.log('[ButtonTest WS] Button room linked:', data);

    setButtons(prev => prev.map(btn =>
      btn.resultId === data.resultId
        ? { ...btn, linkedRoomId: data.roomId, linkedRoomName: data.roomName }
        : btn
    ));
  }, [testUUID]);

  const handleTestCompleted = useCallback((data) => {
    if (data.testUUID !== testUUID) return;
    console.log('[ButtonTest WS] Test completed:', data);

    setIsTestActive(false);

    // Start cooldown period if specified
    const cooldownSecs = data.cooldownSeconds || 5;
    setIsInCooldown(true);
    setCooldownSeconds(cooldownSecs);

    // Countdown timer
    let remaining = cooldownSecs;
    cooldownTimerRef.current = setInterval(() => {
      remaining -= 1;
      setCooldownSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
        setIsInCooldown(false);
        setCooldownSeconds(0);
      }
    }, 1000);

    setSuccess(`Test completed! ${data.testedCount} buttons tested, ${data.skippedCount} skipped. Alarm suppression active for ${cooldownSecs} seconds.`);
    if (onTestComplete) {
      onTestComplete(data);
    }
  }, [testUUID, onTestComplete]);

  // Subscribe to WebSocket events when test is active
  useEffect(() => {
    if (isTestActive && testUUID) {
      console.log('[ButtonTest WS] Subscribing to test:', testUUID);

      unsubscribeRef.current = enoceanService.subscribeToTest(testUUID, {
        onButtonTested: handleButtonTested,
        onButtonSkipped: handleButtonSkipped,
        onButtonRenamed: handleButtonRenamed,
        onButtonRoomLinked: handleButtonRoomLinked,
        onTestCompleted: handleTestCompleted
      });

      return () => {
        if (unsubscribeRef.current) {
          console.log('[ButtonTest WS] Unsubscribing from test:', testUUID);
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    }
  }, [isTestActive, testUUID, handleButtonTested, handleButtonSkipped, handleButtonRenamed, handleButtonRoomLinked, handleTestCompleted]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // Clear cooldown timer on close
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      setIsInCooldown(false);
      setCooldownSeconds(0);
    }
  }, [open]);

  const fetchTestStatus = async () => {
    if (!testUUID) return;

    try {
      const response = await buttonTestsApi.getTestStatus(testUUID);
      if (response.data?.success) {
        const data = response.data.data;
        setButtons(data.buttons || []);
        setTestedCount(data.testedCount || 0);
        setSkippedCount(data.skippedCount || 0);
        setTotalButtons(data.totalButtons || 0);

        if (data.status === 'completed') {
          setIsTestActive(false);
          setSuccess(`Test completed! ${data.testedCount} buttons tested, ${data.skippedCount} skipped.`);
          if (onTestComplete) {
            onTestComplete(data);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching test status:', err);
    }
  };

  const handleStartTest = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await buttonTestsApi.startTest({
        locationId,
        includeSublocations: false
      });

      if (response.data?.success) {
        const data = response.data.data;
        setTestUUID(data.testUUID);
        setButtons(data.buttons || []);
        setTotalButtons(data.totalButtons || 0);
        setTestedCount(0);
        setSkippedCount(0);
        setIsTestActive(true);
        setSuccess('Test started! Press any button to test it.');

        // Fetch naming suggestions
        fetchNamingSuggestions(data.testUUID);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start test');
    } finally {
      setLoading(false);
    }
  };

  const handleEndTest = async () => {
    if (!testUUID) return;

    setLoading(true);
    setError(null);

    try {
      const response = await buttonTestsApi.endTest(testUUID, {});

      if (response.data?.success) {
        const data = response.data.data;
        setIsTestActive(false);

        // Start cooldown period if specified
        const cooldownSecs = data.cooldownSeconds || 5;
        setIsInCooldown(true);
        setCooldownSeconds(cooldownSecs);

        // Countdown timer
        let remaining = cooldownSecs;
        cooldownTimerRef.current = setInterval(() => {
          remaining -= 1;
          setCooldownSeconds(remaining);
          if (remaining <= 0) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
            setIsInCooldown(false);
            setCooldownSeconds(0);
          }
        }, 1000);

        setSuccess(`Test ended. ${data.testedCount} buttons tested, ${data.skippedCount} skipped. Alarm suppression active for ${cooldownSecs} seconds.`);
        if (onTestComplete) {
          onTestComplete(data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to end test');
    } finally {
      setLoading(false);
    }
  };

  // Open skip dialog - require reason before skipping
  const handleOpenSkipDialog = (button) => {
    setButtonToSkip(button);
    setSkipReason('');
    setSkipDialogOpen(true);
  };

  // Confirm skip with reason
  const handleConfirmSkip = async () => {
    if (!testUUID || !buttonToSkip) return;
    if (skipReason.trim().length < 3) {
      setError('Please provide a reason for skipping (at least 3 characters)');
      return;
    }

    try {
      await buttonTestsApi.skipButton(testUUID, buttonToSkip.resultId, { reason: skipReason.trim() });
      // WebSocket will update the UI via handleButtonSkipped
      setSkipDialogOpen(false);
      setButtonToSkip(null);
      setSkipReason('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to skip button');
    }
  };

  // Cancel skip
  const handleCancelSkip = () => {
    setSkipDialogOpen(false);
    setButtonToSkip(null);
    setSkipReason('');
  };

  // Fetch rooms for a location (for fixed buttons linked to Unit locations)
  // Gets all descendant rooms, not just direct children
  const fetchRoomsForLocation = async (locId) => {
    try {
      // Recursive function to get all descendants
      const getAllDescendants = async (parentId, collected = []) => {
        const response = await locationsApi.getChildren(parentId);
        const children = response?.data?.data || response?.data || [];

        for (const child of children) {
          collected.push(child);
          // Recursively get children of this location
          await getAllDescendants(child.id, collected);
        }

        return collected;
      };

      const allDescendants = await getAllDescendants(locId);

      // Filter for rooms (locations that are likely rooms - could be based on locationType)
      // Include all descendants that could be rooms
      const rooms = allDescendants.filter(loc =>
        loc.locationType === 'room' ||
        loc.locationType === 3 || // Room type ID
        loc.locationTypeModel?.name?.toLowerCase() === 'room' ||
        loc.locationTypeModel?.friendlyName?.toLowerCase()?.includes('room') ||
        loc.locationTypeModel?.friendlyName?.toLowerCase()?.includes('kamer') ||
        // Include all locations at deeper levels (likely rooms under units)
        true
      );

      setLinkedRooms(rooms);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setLinkedRooms([]);
    }
  };

  // Open room link dialog for fixed buttons
  const handleOpenRoomLinkDialog = (button) => {
    setButtonForRoomLink(button);
    setRoomLinkDialogOpen(true);
    fetchRoomsForLocation(locationId);
  };

  // Close room link dialog
  const handleCloseRoomLinkDialog = () => {
    setRoomLinkDialogOpen(false);
    setButtonForRoomLink(null);
    setLinkedRooms([]);
  };

  // Link fixed button to a room
  const handleLinkButtonToRoom = async (roomId, roomName) => {
    if (!testUUID || !buttonForRoomLink) return;

    try {
      // Update button with room information
      await buttonTestsApi.updateButtonRoom(testUUID, buttonForRoomLink.resultId, roomId);

      // Update local state
      setButtons(prev => prev.map(btn =>
        btn.resultId === buttonForRoomLink.resultId
          ? { ...btn, linkedRoomId: roomId, linkedRoomName: roomName }
          : btn
      ));

      setSuccess(`Button linked to room "${roomName}"`);
      handleCloseRoomLinkDialog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link button to room');
    }
  };

  // Download test report (CSV/JSON)
  const handleDownloadReport = async (format = 'csv') => {
    if (!testUUID) return;

    try {
      const response = await buttonTestsApi.getTestReport(testUUID, format);

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `button-test-report-${testUUID}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        setSuccess('Test report downloaded successfully');
      } else {
        const blob = new Blob([JSON.stringify(response.data?.data || response.data, null, 2)], {
          type: 'application/json'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `button-test-report-${testUUID}.json`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        setSuccess('Test report downloaded successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download report');
    }
  };

  // Download test report as PDF
  const handleDownloadPdfReport = async () => {
    if (!testUUID) return;

    try {
      // Fetch the JSON report data for PDF generation
      const response = await buttonTestsApi.getTestReport(testUUID, 'json');
      const reportData = response.data?.data || response.data;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const headerHeight = 25;
      const footerHeight = 15;
      const contentWidth = pageWidth - (2 * margin);
      const contentAreaTop = margin + headerHeight;
      const contentAreaBottom = pageHeight - footerHeight;
      let yPosition = contentAreaTop;
      let currentPage = 1;

      // Brand colors (CARO style)
      const plumMain = [154, 63, 105];
      const grey800 = [28, 37, 46];
      const grey500 = [145, 158, 171];
      const grey300 = [223, 227, 232];
      const successColor = [46, 125, 50];
      const warningColor = [237, 108, 2];
      const errorColor = [211, 47, 47];

      // Helper: add header
      const addHeader = async () => {
        pdf.setDrawColor(...plumMain);
        pdf.setLineWidth(0.5);
        pdf.line(margin, margin + 8, pageWidth - margin, margin + 8);

        try {
          const logoImg = new Image();
          logoImg.src = '/logo/logo-full.png';

          if (!logoImg.complete) {
            await new Promise((resolve, reject) => {
              logoImg.onload = resolve;
              logoImg.onerror = () => {
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(...plumMain);
                pdf.text('CARO Technology', margin, margin + 5);
                resolve();
              };
              setTimeout(reject, 1000);
            });
          }

          if (logoImg.complete && logoImg.naturalHeight !== 0) {
            const logoHeight = 6;
            const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
            pdf.addImage(logoImg, 'PNG', margin, margin, logoWidth, logoHeight);
          } else {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...plumMain);
            pdf.text('CARO Technology', margin, margin + 5);
          }
        } catch (err) {
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...plumMain);
          pdf.text('CARO Technology', margin, margin + 5);
        }
      };

      // Helper: add footer
      const addFooter = (pageNum) => {
        const footerY = pageHeight - footerHeight + 5;
        pdf.setDrawColor(...grey300);
        pdf.setLineWidth(0.3);
        pdf.line(margin, footerY, pageWidth - margin, footerY);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...grey500);
        pdf.text(`© ${new Date().getFullYear()} CARO Technology`, margin, footerY + 5);
        pdf.text(`Page ${pageNum}`, pageWidth - margin - 15, footerY + 5);
      };

      // Helper: check page break
      const checkPageBreak = async (requiredSpace = 10) => {
        if (yPosition + requiredSpace > contentAreaBottom) {
          addFooter(currentPage);
          pdf.addPage();
          currentPage++;
          await addHeader();
          yPosition = contentAreaTop;
          return true;
        }
        return false;
      };

      // --- Page 1: Header ---
      await addHeader();

      // Title
      pdf.setTextColor(...plumMain);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Button Test Report', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey500);
      pdf.text('CARO Technology - Emergency Button Testing', margin, yPosition);
      yPosition += 5;

      pdf.setFontSize(8);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, yPosition);
      yPosition += 3;

      // Divider
      pdf.setFillColor(...plumMain);
      pdf.rect(margin, yPosition, contentWidth, 0.5, 'F');
      yPosition += 8;

      // Test info
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey800);
      pdf.text('Test Details', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');

      const testInfo = [
        ['Location:', locationName || 'N/A'],
        ['Test UUID:', testUUID],
        ['Status:', reportData.status || 'completed'],
        ['Started:', reportData.startedAt ? new Date(reportData.startedAt).toLocaleString() : 'N/A'],
        ['Completed:', reportData.endedAt ? new Date(reportData.endedAt).toLocaleString() : 'N/A'],
      ];

      for (const [label, value] of testInfo) {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...grey500);
        pdf.text(label, margin + 2, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...grey800);
        pdf.text(String(value), margin + 30, yPosition);
        yPosition += 5;
      }
      yPosition += 5;

      // --- Summary Stats Boxes ---
      const reportButtons = reportData.buttons || buttons;
      const rTested = reportButtons.filter(b => b.status === 'success').length;
      const rSkipped = reportButtons.filter(b => b.status === 'skipped').length;
      const rPending = reportButtons.filter(b => b.status === 'pending').length;
      const rTotal = reportButtons.length;

      await checkPageBreak(30);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey800);
      pdf.text('Summary', margin, yPosition);
      yPosition += 6;

      const boxWidth = (contentWidth - 6) / 3;
      const boxHeight = 18;

      // Tested box
      pdf.setFillColor(232, 245, 233);
      pdf.setDrawColor(...successColor);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, yPosition, boxWidth, boxHeight, 1, 1, 'FD');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...successColor);
      pdf.text(String(rTested), margin + boxWidth / 2, yPosition + 9, { align: 'center' });
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Tested', margin + boxWidth / 2, yPosition + 14, { align: 'center' });

      // Skipped box
      const box2X = margin + boxWidth + 3;
      pdf.setFillColor(255, 243, 224);
      pdf.setDrawColor(...warningColor);
      pdf.roundedRect(box2X, yPosition, boxWidth, boxHeight, 1, 1, 'FD');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...warningColor);
      pdf.text(String(rSkipped), box2X + boxWidth / 2, yPosition + 9, { align: 'center' });
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Skipped', box2X + boxWidth / 2, yPosition + 14, { align: 'center' });

      // Total box
      const box3X = margin + (boxWidth + 3) * 2;
      pdf.setFillColor(245, 243, 244);
      pdf.setDrawColor(...plumMain);
      pdf.roundedRect(box3X, yPosition, boxWidth, boxHeight, 1, 1, 'FD');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...plumMain);
      pdf.text(String(rTotal), box3X + boxWidth / 2, yPosition + 9, { align: 'center' });
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total', box3X + boxWidth / 2, yPosition + 14, { align: 'center' });

      yPosition += boxHeight + 10;

      // --- Button Details Table ---
      await checkPageBreak(20);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey800);
      pdf.text('Button Details', margin, yPosition);
      yPosition += 6;

      // Table header
      const colWidths = [45, 25, 25, 30, contentWidth - 125];
      const colLabels = ['Button Name', 'Status', 'Type', 'Signal', 'Details'];
      const colX = [margin];
      for (let i = 1; i < colWidths.length; i++) {
        colX.push(colX[i - 1] + colWidths[i - 1]);
      }

      pdf.setFillColor(245, 243, 244);
      pdf.setDrawColor(...grey300);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, yPosition, contentWidth, 7, 'FD');

      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey800);
      for (let i = 0; i < colLabels.length; i++) {
        pdf.text(colLabels[i], colX[i] + 2, yPosition + 5);
      }
      yPosition += 7;

      // Table rows
      for (const btn of reportButtons) {
        await checkPageBreak(12);

        const rowHeight = 8;
        pdf.setDrawColor(...grey300);
        pdf.setLineWidth(0.1);
        pdf.line(margin, yPosition + rowHeight, margin + contentWidth, yPosition + rowHeight);

        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...grey800);

        // Button name
        const btnName = btn.friendlyName || btn.person || btn.location || `Button ${btn.hexCode}`;
        const nameText = btnName.length > 25 ? btnName.substring(0, 22) + '...' : btnName;
        pdf.text(nameText, colX[0] + 2, yPosition + 5);

        // Status
        if (btn.status === 'success') {
          pdf.setTextColor(...successColor);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Tested', colX[1] + 2, yPosition + 5);
        } else if (btn.status === 'skipped') {
          pdf.setTextColor(...warningColor);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Skipped', colX[1] + 2, yPosition + 5);
        } else {
          pdf.setTextColor(...grey500);
          pdf.text('Pending', colX[1] + 2, yPosition + 5);
        }

        // Type
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...grey800);
        const typeText = btn.assignmentType === 'mobile' ? 'Mobile' : 'Fixed';
        pdf.text(typeText, colX[2] + 2, yPosition + 5);

        // Signal
        if (btn.rssiDbm !== null && btn.rssiDbm !== undefined) {
          const sig = getSignalInfo(btn.rssiDbm);
          if (sig.color === 'success') pdf.setTextColor(...successColor);
          else if (sig.color === 'warning') pdf.setTextColor(...warningColor);
          else if (sig.color === 'error') pdf.setTextColor(...errorColor);
          else pdf.setTextColor(...grey500);
          pdf.text(`${sig.label} (${btn.rssiDbm}dBm)`, colX[3] + 2, yPosition + 5);
        } else {
          pdf.setTextColor(...grey500);
          pdf.text('-', colX[3] + 2, yPosition + 5);
        }

        // Details
        pdf.setTextColor(...grey800);
        const details = [];
        if (btn.person) details.push(`Person: ${btn.person}`);
        if (btn.linkedRoomName) details.push(`Room: ${btn.linkedRoomName}`);
        if (btn.antennaName) details.push(`Antenna: ${btn.antennaName}`);
        if (btn.skipReason) details.push(`Reason: ${btn.skipReason}`);
        const detailText = details.join(' | ');
        const trimmedDetail = detailText.length > 45 ? detailText.substring(0, 42) + '...' : detailText;
        pdf.text(trimmedDetail || '-', colX[4] + 2, yPosition + 5);

        yPosition += rowHeight;
      }

      // Add footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i);
      }

      // Save
      pdf.save(`button-test-report-${testUUID}.pdf`);
      setSuccess('PDF report downloaded successfully');
    } catch (err) {
      console.error('Error generating PDF report:', err);
      setError(err.response?.data?.error || 'Failed to generate PDF report');
    }
  };

  const fetchNamingSuggestions = async (uuid) => {
    try {
      const response = await buttonTestsApi.getNamingSuggestions(uuid);
      if (response.data?.success) {
        setNamingSuggestions(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching naming suggestions:', err);
    }
  };

  const handleOpenNameMenu = (event, button) => {
    setNameMenuAnchor(event.currentTarget);
    setSelectedButtonForNaming(button);
  };

  const handleCloseNameMenu = () => {
    setNameMenuAnchor(null);
    setSelectedButtonForNaming(null);
  };

  const handleSelectName = async (name) => {
    if (!testUUID || !selectedButtonForNaming) return;

    try {
      await buttonTestsApi.updateButtonName(testUUID, selectedButtonForNaming.resultId, name);
      // WebSocket will update the UI via handleButtonRenamed
      handleCloseNameMenu();
      setSuccess(`Button renamed to "${name}"`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update button name');
    }
  };

  const handleStartEditName = (button) => {
    setEditingButtonId(button.resultId);
    setEditName(button.friendlyName || '');
  };

  const handleSaveName = async (resultId) => {
    if (!testUUID || !editName.trim()) return;

    try {
      await buttonTestsApi.updateButtonName(testUUID, resultId, editName.trim());
      // WebSocket will update the UI via handleButtonRenamed
      setEditingButtonId(null);
      setEditName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update button name');
    }
  };

  const handleCancelEditName = () => {
    setEditingButtonId(null);
    setEditName('');
  };

  const handleClose = () => {
    if (isTestActive) {
      handleEndTest();
    }
    // Unsubscribe from WebSocket
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    // Clear cooldown timer
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    setTestUUID(null);
    setButtons([]);
    setIsTestActive(false);
    setIsInCooldown(false);
    setCooldownSeconds(0);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <SuccessIcon color="success" />;
      case 'skipped':
        return <SkipIcon color="warning" />;
      case 'pending':
      default:
        return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'skipped':
        return 'warning';
      case 'pending':
      default:
        return 'default';
    }
  };

  const pendingButtons = buttons.filter(b => b.status === 'pending');
  const testedButtons = buttons.filter(b => b.status === 'success');
  const skippedButtons = buttons.filter(b => b.status === 'skipped');

  const progress = totalButtons > 0 ? ((testedCount + skippedCount) / totalButtons) * 100 : 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <TouchAppIcon color="primary" />
              <Typography variant="h6">
                Button Test
              </Typography>
            </Box>
            {isTestActive && (
              <Chip
                icon={<CircularProgress size={16} />}
                label="Test Active"
                color="success"
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Location: {locationName}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {/* Error/Success Messages */}
          <Collapse in={!!error}>
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Collapse>
          <Collapse in={!!success}>
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          </Collapse>

          {/* Progress Bar */}
          {isTestActive && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: {testedCount + skippedCount} / {totalButtons}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  icon={<SuccessIcon />}
                  label={`Tested: ${testedCount}`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<SkipIcon />}
                  label={`Skipped: ${skippedCount}`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<PendingIcon />}
                  label={`Pending: ${pendingButtons.length}`}
                  color="default"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}

          {/* Start Test Button */}
          {!isTestActive && buttons.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 2,
                mb: 2
              }}
            >
              <TouchAppIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Ready to Test Buttons
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start a test session to verify all emergency buttons at this location.
                Press each button to confirm it's working properly.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
                onClick={handleStartTest}
                disabled={loading}
              >
                Start Button Test
              </Button>
            </Paper>
          )}

          {/* Test Instructions */}
          {isTestActive && pendingButtons.length > 0 && (
            <Alert severity="info" icon={<TouchAppIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Press any emergency button</strong> to test it. The button will be marked as tested when received.
                You can also skip buttons that you don't want to test.
              </Typography>
            </Alert>
          )}

          {/* Buttons List */}
          {buttons.length > 0 && (
            <Box>
              {/* Pending Buttons */}
              {pendingButtons.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Pending ({pendingButtons.length})
                  </Typography>
                  <List dense>
                    {pendingButtons.map((button) => (
                      <ListItem
                        key={button.resultId}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          animation: isTestActive ? 'pulse 2s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.7 }
                          }
                        }}
                      >
                        <ListItemIcon>
                          {isTestActive ? (
                            <CircularProgress size={24} />
                          ) : (
                            <ButtonIcon color="primary" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            editingButtonId === button.resultId ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                  size="small"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  autoFocus
                                  sx={{ flex: 1 }}
                                />
                                <IconButton size="small" onClick={() => handleSaveName(button.resultId)}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEditName}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">
                                  {button.friendlyName || button.person || button.location || `Button ${button.hexCode}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={button.buttonType || 'emergency'}
                                  color={button.buttonType === 'emergency' ? 'error' : 'default'}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={button.assignmentType === 'mobile' ? 'Mobile' : 'Fixed'}
                                  color={button.assignmentType === 'mobile' ? 'info' : 'secondary'}
                                  variant="outlined"
                                />
                              </Box>
                            )
                          }
                          secondary={
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              <Typography variant="caption" fontFamily="monospace">
                                HEX: {button.hexCode}
                              </Typography>
                              {/* Show person for mobile buttons */}
                              {button.assignmentType === 'mobile' && button.person && (
                                <Chip
                                  size="small"
                                  icon={<PersonIcon />}
                                  label={button.person}
                                  variant="outlined"
                                />
                              )}
                              {/* Show room status for mobile buttons (person's room) */}
                              {button.assignmentType === 'mobile' && button.linkedRoomName && (
                                <Chip
                                  size="small"
                                  icon={<RoomIcon />}
                                  label={button.linkedRoomName}
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                              {/* Show room link status for fixed buttons */}
                              {button.assignmentType === 'fixed' && (
                                button.linkedRoomId ? (
                                  <Chip
                                    size="small"
                                    icon={<RoomIcon />}
                                    label={button.linkedRoomName || 'Room linked'}
                                    color="success"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    icon={<WarningIcon />}
                                    label="Room required!"
                                    color="error"
                                    variant="outlined"
                                  />
                                )
                              )}
                              {/* Show location if button is at parent level (not in a room) */}
                              {button.location && !button.linkedRoomId && button.assignmentType === 'fixed' && (
                                <Chip
                                  size="small"
                                  icon={<LocationIcon />}
                                  label={button.location}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {/* Room link button for fixed buttons */}
                            {button.assignmentType === 'fixed' && (
                              <Tooltip title={button.linkedRoomId ? `Linked to: ${button.linkedRoomName}` : "Link to Room (Required for fixed buttons)"}>
                                <IconButton
                                  size="small"
                                  color={button.linkedRoomId ? "success" : "error"}
                                  onClick={() => handleOpenRoomLinkDialog(button)}
                                >
                                  <RoomIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!button.friendlyName && (
                              <Tooltip title="Set Name">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenNameMenu(e, button)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Skip Button">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenSkipDialog(button)}
                              >
                                <SkipIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Tested Buttons */}
              {testedButtons.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'success.main' }}>
                    Tested ({testedButtons.length})
                  </Typography>
                  <List dense>
                    {testedButtons.map((button) => {
                      const signalInfo = getSignalInfo(button.rssiDbm);
                      return (
                        <ListItem
                          key={button.resultId}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: 1,
                            borderColor: 'success.main',
                            bgcolor: 'success.light',
                            opacity: 0.9
                          }}
                        >
                          <ListItemIcon>
                            <SuccessIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">
                                  {button.friendlyName || button.person || button.location || `Button ${button.hexCode}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  label="Tested"
                                  color="success"
                                />
                              </Box>
                            }
                            secondary={
                              <Box display="flex" alignItems="center" gap={2}>
                                <Tooltip title={`Signal: ${signalInfo.label} (${button.rssiDbm} dBm)`}>
                                  <Chip
                                    size="small"
                                    icon={<SignalIcon />}
                                    label={signalInfo.label}
                                    color={signalInfo.color}
                                    variant="outlined"
                                  />
                                </Tooltip>
                                {button.antennaName && (
                                  <Typography variant="caption" color="text.secondary">
                                    via {button.antennaName}
                                  </Typography>
                                )}
                                {button.channel && (
                                  <Typography variant="caption" color="text.secondary">
                                    Channel: {button.channel}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {/* Skipped Buttons */}
              {skippedButtons.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'warning.main' }}>
                    Skipped ({skippedButtons.length})
                  </Typography>
                  <List dense>
                    {skippedButtons.map((button) => (
                      <ListItem
                        key={button.resultId}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          border: 1,
                          borderColor: 'warning.main',
                          bgcolor: 'warning.light',
                          opacity: 0.7
                        }}
                      >
                        <ListItemIcon>
                          <SkipIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={button.friendlyName || button.person || button.location || `Button ${button.hexCode}`}
                          secondary={
                            <Box>
                              <Typography variant="caption" fontFamily="monospace" display="block">
                                HEX: {button.hexCode}
                              </Typography>
                              {button.skipReason && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  <strong>Reason:</strong> {button.skipReason}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {/* Cooldown Alert */}
          {isInCooldown && (
            <Alert
              severity="info"
              icon={<TimerIcon />}
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Alarm suppression active</strong> - Button presses will not trigger alarms for {cooldownSeconds} more second{cooldownSeconds !== 1 ? 's' : ''}.
                </Typography>
                <CircularProgress size={20} />
              </Box>
            </Alert>
          )}

          {/* Test Complete Summary */}
          {!isTestActive && buttons.length > 0 && (testedCount > 0 || skippedCount > 0) && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Test Complete
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadPdfReport}
                    >
                      Download PDF
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleDownloadReport('csv')}
                    >
                      CSV
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">{testedCount}</Typography>
                      <Typography variant="body2" color="text.secondary">Tested</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">{skippedCount}</Typography>
                      <Typography variant="body2" color="text.secondary">Skipped</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">{totalButtons}</Typography>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <DialogActions>
          {isTestActive ? (
            <>
              <Button onClick={handleClose} color="inherit">
                Close (End Test)
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleEndTest}
                disabled={loading}
              >
                End Test
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose}>
                Close
              </Button>
              {buttons.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<StartIcon />}
                  onClick={handleStartTest}
                  disabled={loading}
                >
                  Start New Test
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Naming Suggestions Menu */}
      <Menu
        anchorEl={nameMenuAnchor}
        open={Boolean(nameMenuAnchor)}
        onClose={handleCloseNameMenu}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Suggest from Rooms
        </Typography>
        {namingSuggestions.rooms.length > 0 ? (
          namingSuggestions.rooms.map((room) => (
            <MenuItem key={room.id} onClick={() => handleSelectName(room.name)}>
              <ListItemIcon>
                <RoomIcon fontSize="small" />
              </ListItemIcon>
              {room.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No rooms found</Typography>
          </MenuItem>
        )}
        <Divider />
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Suggest from Persons
        </Typography>
        {namingSuggestions.persons.length > 0 ? (
          namingSuggestions.persons.map((person) => (
            <MenuItem key={person.id} onClick={() => handleSelectName(person.name)}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {person.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No persons found</Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => {
          handleCloseNameMenu();
          if (selectedButtonForNaming) {
            handleStartEditName(selectedButtonForNaming);
          }
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Custom Name...
        </MenuItem>
      </Menu>

      {/* Skip Reason Dialog */}
      <Dialog
        open={skipDialogOpen}
        onClose={handleCancelSkip}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SkipIcon color="warning" />
            <Typography variant="h6">Skip Button</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are about to skip testing button: <strong>{buttonToSkip?.friendlyName || buttonToSkip?.hexCode}</strong>
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please provide a reason for skipping this button. This is required for audit purposes.
          </Alert>
          <TextField
            fullWidth
            label="Reason for skipping"
            placeholder="e.g., Button not accessible, Button removed, etc."
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            required
            error={skipReason.length > 0 && skipReason.length < 3}
            helperText={
              skipReason.length > 0 && skipReason.length < 3
                ? `Minimum 3 characters required (${skipReason.length}/3)`
                : `${skipReason.length} characters`
            }
            autoFocus
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSkip} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSkip}
            variant="contained"
            color="warning"
            disabled={skipReason.trim().length < 3}
            startIcon={<SkipIcon />}
          >
            Skip Button
          </Button>
        </DialogActions>
      </Dialog>

      {/* Room Link Dialog for Fixed Buttons */}
      <Dialog
        open={roomLinkDialogOpen}
        onClose={handleCloseRoomLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <RoomIcon color="primary" />
            <Typography variant="h6">Link Button to Room</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fixed button: <strong>{buttonForRoomLink?.friendlyName || buttonForRoomLink?.hexCode}</strong>
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Fixed buttons must be linked to a specific room for accurate alarm reporting.
            Select the room where this button is physically installed.
          </Alert>

          {linkedRooms.length > 0 ? (
            <List>
              {linkedRooms.map((room) => (
                <ListItem
                  key={room.id}
                  button
                  onClick={() => handleLinkButtonToRoom(room.id, room.friendlyName || room.name)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    border: 1,
                    borderColor: buttonForRoomLink?.linkedRoomId === room.id ? 'primary.main' : 'divider',
                    bgcolor: buttonForRoomLink?.linkedRoomId === room.id ? 'primary.light' : 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <RoomIcon color={buttonForRoomLink?.linkedRoomId === room.id ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={room.friendlyName || room.name}
                    secondary={room.path || room.description}
                  />
                  {buttonForRoomLink?.linkedRoomId === room.id && (
                    <CheckIcon color="primary" />
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="warning">
              No rooms found in this location. Please add rooms to this location first, or check that this location is a Unit with child rooms.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoomLinkDialog} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ButtonTestDialog;
